import {AppActions, RootState} from 'app/rootReducer';
import Toast from 'components/Basic/Toast';
import {userActions} from 'features/User';
import {SliceStatus, SuperCoachChatMessageStreamed} from 'interfaces';
import {Epic, ofType} from 'redux-observable';
import {
  catchError,
  concat,
  concatMap,
  filter,
  from,
  ignoreElements,
  mapTo,
  mergeMap,
  Observable,
  of,
  switchMap,
  takeUntil,
  tap,
  timer,
  withLatestFrom,
} from 'rxjs';
import {SuperCoachService} from 'services/api';

import {superCoachActions} from './superCoachSlice';

const POLL_INTERVAL = 5 * 60 * 1000;

/**
 * Polling Epic:
 * - Listens for Redux init (so it fires once at app‑start),
 * - Starts a timer(0, POLL_INTERVAL) to dispatch getSuperCoachRecommendations()
 * - Stops if the user logs out
 */
export const recommendationsPollingEpic: Epic<
  AppActions,
  AppActions,
  RootState
> = action$ =>
  action$.pipe(
    ofType('@@redux/INIT') as any,
    switchMap(() =>
      timer(0, POLL_INTERVAL).pipe(
        mapTo(superCoachActions.getSuperCoachRecommendations()),
        takeUntil(action$.pipe(ofType(userActions.logout.type))),
      ),
    ),
  );

/**
 * Fetch recommendations.
 */
export const fetchRecommendationsEpic: Epic<
  AppActions,
  AppActions,
  RootState
> = (action$, state$) =>
  action$.pipe(
    filter(superCoachActions.getSuperCoachRecommendations.match),
    withLatestFrom(state$),
    filter(
      ([_, state]) =>
        state.superCoach.sendChatMessageStatus !== SliceStatus.pending ||
        state.superCoach.getMessagesStatus !== SliceStatus.pending,
    ),
    mergeMap(() =>
      from(SuperCoachService.getSuperCoachRecommendations()).pipe(
        concatMap(resp =>
          of(
            superCoachActions.getSuperCoachRecommendationsSuccess(
              resp.data.message,
            ),
          ),
        ),
        catchError((err: Error) =>
          of(
            superCoachActions.getSuperCoachRecommendationsFailure(err.message),
          ),
        ),
      ),
    ),
  );

export const sendSuperCoachOnboardingDataEpic: Epic<
  AppActions,
  AppActions,
  RootState
> = action$ =>
  action$.pipe(
    filter(superCoachActions.sendSuperCoachOnboardingData.match),
    mergeMap(({payload: {onSuccess, ...payload}}) =>
      from(SuperCoachService.sendSuperCoachOnboardingData(payload)).pipe(
        concatMap(() => {
          if (onSuccess) {
            onSuccess();
          }

          return of(
            superCoachActions.sendSuperCoachOnboardingDataSuccess(true),
          );
        }),
        catchError((message: string) =>
          concat(
            of(superCoachActions.sendSuperCoachOnboardingDataFailure(message)),
          ),
        ),
      ),
    ),
  );

export const sendSuperCoachOnboardingFailureEpic: Epic<
  AppActions,
  AppActions,
  RootState
> = action$ =>
  action$.pipe(
    filter(superCoachActions.sendSuperCoachOnboardingDataFailure.match),
    tap(({payload: message}) => {
      if (message) {
        Toast({type: 'error', message});
      }
    }),
    ignoreElements(),
  );

export const getSuperCoachChatMessagesEpic: Epic<
  AppActions,
  AppActions,
  RootState
> = action$ =>
  action$.pipe(
    filter(superCoachActions.getSuperCoachChatMessages.match),
    mergeMap(({payload: {onSuccess, ...payload}}) =>
      from(SuperCoachService.getSuperCoachChatMessages(payload)).pipe(
        concatMap(result => {
          const pipes = of(
            superCoachActions.getSuperCoachChatMessagesSuccess(
              result.data.message,
            ),
          );
          if (onSuccess) {
            onSuccess(result.data.message);
          }
          return pipes;
        }),

        catchError((message: string) =>
          concat(
            of(superCoachActions.getSuperCoachChatMessagesFailure(message)),
          ),
        ),
      ),
    ),
  );

export const getSuperCoachChatMessagesFailureEpic: Epic<
  AppActions,
  AppActions,
  RootState
> = action$ =>
  action$.pipe(
    filter(superCoachActions.getSuperCoachChatMessagesFailure.match),
    tap(({payload: message}) => {
      if (message) {
        Toast({type: 'error', message});
      }
    }),
    ignoreElements(),
  );

export const sendSuperCoachChatMessageEpic: Epic = action$ =>
  action$.pipe(
    filter(superCoachActions.sendSuperCoachChatMessage.match),
    mergeMap(
      ({payload}) =>
        new Observable(observer => {
          const eventSource =
            SuperCoachService.sendSuperCoachChatMessage(payload);
          let response = ``;

          eventSource.onmessage = event => {
            try {
              const parsedData: SuperCoachChatMessageStreamed = JSON.parse(
                event.data,
              );

              if (parsedData.done) {
                eventSource.close();

                // Close the connection immediately, but delay calling the action to let the animations finish
                setTimeout(() => {
                  observer.next(
                    superCoachActions.endSuperCoachChatMessageStreamChunk({
                      message: parsedData.message,
                      recommendedMessage: parsedData.recommendedMessage,
                    }),
                  );
                  observer.complete();
                }, response.length * 10);
                return;
              }

              if (parsedData.text) {
                response += parsedData.text;
                observer.next(
                  superCoachActions.receiveSuperCoachChatMessageStreamChunk({
                    text: parsedData.text,
                  }),
                );
              }
            } catch (error) {
              observer.next(
                superCoachActions.sendSuperCoachChatMessageFailure(
                  'Error parsing event data',
                ),
              );
            }
          };

          eventSource.onerror = () => {
            observer.next(
              superCoachActions.sendSuperCoachChatMessageFailure('API error'),
            );
            observer.complete();
            eventSource?.close();
          };

          return () => {
            eventSource?.close();
          };
        }),
    ),
  );

export const sendSuperCoachChatMessageFailureEpic: Epic<
  AppActions,
  AppActions,
  RootState
> = action$ =>
  action$.pipe(
    filter(superCoachActions.sendSuperCoachChatMessageFailure.match),
    tap(({payload: message}) => {
      if (message) {
        Toast({type: 'error', message});
      }
    }),
    ignoreElements(),
  );

export const saveSuperCoachActionFeedbackEpic: Epic<
  AppActions,
  AppActions,
  RootState
> = action$ =>
  action$.pipe(
    filter(superCoachActions.saveSuperCoachActionFeedback.match),
    mergeMap(({payload}) =>
      from(SuperCoachService.saveSuperCoachActionFeedback(payload)).pipe(
        concatMap(() => {
          return of(superCoachActions.saveSuperCoachActionFeedbackSuccess());
        }),
        catchError((message: string) =>
          concat(
            of(superCoachActions.saveSuperCoachActionFeedbackFailure(message)),
          ),
        ),
      ),
    ),
  );

export const saveSuperCoachActionFeedbackFailureEpic: Epic<
  AppActions,
  AppActions,
  RootState
> = action$ =>
  action$.pipe(
    filter(superCoachActions.saveSuperCoachActionFeedbackFailure.match),
    tap(({payload: message}) => {
      if (message) {
        Toast({type: 'error', message});
      }
    }),
    ignoreElements(),
  );

export const superCoachEpics = [
  fetchRecommendationsEpic,
  sendSuperCoachOnboardingDataEpic,
  sendSuperCoachOnboardingFailureEpic,
  getSuperCoachChatMessagesEpic,
  getSuperCoachChatMessagesFailureEpic,
  sendSuperCoachChatMessageEpic,
  sendSuperCoachChatMessageFailureEpic,
  saveSuperCoachActionFeedbackEpic,
  saveSuperCoachActionFeedbackFailureEpic,
];
